interface Certification {
  name: string;
  issuer: string;
  year: string;
  icon: string;
  color: string;
}

interface FactoryCertificationsProps {
  certifications: Certification[];
  factoryName: string;
  founded: string;
  employees: string;
  annualRevenue: string;
  moq: string;
  leadTime: string;
  exportCountries: string[];
  about: string;
}

export default function FactoryCertifications({
  certifications,
  factoryName,
  founded,
  employees,
  annualRevenue,
  moq,
  leadTime,
  exportCountries,
  about,
}: FactoryCertificationsProps) {
  return (
    <div className="space-y-6">
      {/* Certifications Grid */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg">
            <i className="ri-award-line text-green-600 text-lg" />
          </div>
          <h2 className="text-base font-bold text-gray-900">Certifications & Compliance</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {certifications.map((cert) => (
            <div key={cert.name} className={`flex items-start gap-3 p-4 rounded-xl border ${cert.color}`}>
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                <i className={`${cert.icon} text-lg`} />
              </div>
              <div>
                <p className="text-sm font-bold">{cert.name}</p>
                <p className="text-xs opacity-80 mt-0.5">{cert.issuer}</p>
                <p className="text-xs opacity-60 mt-0.5">Issued {cert.year}</p>
              </div>
              <div className="ml-auto flex-shrink-0">
                <i className="ri-verified-badge-fill text-green-500 text-lg" />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-gray-50 rounded-xl flex items-center gap-2">
          <i className="ri-information-line text-gray-400 text-sm" />
          <p className="text-xs text-gray-500">All certifications are verified by StarBookPrime. Documents available upon request.</p>
        </div>
      </div>

      {/* Factory Details */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg">
            <i className="ri-building-2-line text-orange-500 text-lg" />
          </div>
          <h2 className="text-base font-bold text-gray-900">Factory Profile</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-5">
          {[
            { icon: "ri-calendar-line", label: "Founded", value: founded, color: "text-orange-500 bg-orange-50" },
            { icon: "ri-team-line", label: "Employees", value: employees, color: "text-green-600 bg-green-50" },
            { icon: "ri-money-dollar-circle-line", label: "Annual Revenue", value: annualRevenue, color: "text-orange-500 bg-orange-50" },
            { icon: "ri-stack-line", label: "Min. Order", value: moq, color: "text-amber-600 bg-amber-50" },
            { icon: "ri-time-line", label: "Lead Time", value: leadTime, color: "text-green-600 bg-green-50" },
            { icon: "ri-global-line", label: "Export Markets", value: `${exportCountries.length} countries`, color: "text-orange-500 bg-orange-50" },
          ].map((item) => (
            <div key={item.label} className="bg-gray-50 rounded-xl p-4">
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg mb-2 ${item.color}`}>
                <i className={`${item.icon} text-sm`} />
              </div>
              <p className="text-sm font-bold text-gray-900">{item.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{item.label}</p>
            </div>
          ))}
        </div>

        {/* Export Countries */}
        <div className="mb-5">
          <p className="text-xs font-semibold text-gray-600 mb-2">Export Countries</p>
          <div className="flex flex-wrap gap-2">
            {exportCountries.map((country) => (
              <span key={country} className="px-3 py-1.5 bg-orange-50 border border-orange-100 text-orange-700 text-xs font-medium rounded-full">
                {country}
              </span>
            ))}
          </div>
        </div>

        {/* About */}
        <div className="bg-gray-50 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-600 mb-2">About {factoryName}</p>
          <p className="text-sm text-gray-600 leading-relaxed">{about}</p>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: "ri-shield-check-fill", title: "Identity Verified", desc: "Business registration and ownership documents verified by StarBookPrime team.", color: "text-green-500 bg-green-50" },
          { icon: "ri-file-list-3-fill", title: "Certifications Audited", desc: "All compliance certificates reviewed and confirmed valid by our verification team.", color: "text-orange-500 bg-orange-50" },
          { icon: "ri-customer-service-2-fill", title: "Responsive Seller", desc: "This factory maintains a high response rate and fast reply time to buyer inquiries.", color: "text-amber-500 bg-amber-50" },
        ].map((b) => (
          <div key={b.title} className="bg-white rounded-xl border border-gray-100 p-5 flex items-start gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${b.color}`}>
              <i className={`${b.icon} text-lg`} />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 mb-1">{b.title}</p>
              <p className="text-xs text-gray-500 leading-relaxed">{b.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
